import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

type FailureRecordResult = {
  tripped: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class ApiCircuitBreakerService {
  /** Trip after this many failures in window */
  private readonly failureThreshold = 5;
  /** Failure window in milliseconds */
  private readonly failureWindowMs = 60_000;
  /** Block duration once tripped */
  private readonly cooldownMs = 60_000;

  /** Essential endpoints that should bypass the breaker */
  private readonly allowlistedPathFragments = [
    '/flows/tasks/',
    '/jobs/',
    '/files/download-s3',
    '/health',
    '/healthz'
  ];

  private readonly breakerOpenUntil = signal<number>(0);
  private readonly firstFailureAt = signal<number>(0);
  private readonly consecutiveFailures = signal<number>(0);

  /**
   * Returns true if request can proceed.
   * Allowlisted endpoints are always permitted.
   */
  canRequest(url: string): boolean {
    if (this.isAllowlisted(url)) {
      return true;
    }

    const now = Date.now();
    const openUntil = this.breakerOpenUntil();

    if (openUntil > now) {
      return false;
    }

    // Auto-close breaker after cooldown
    if (openUntil !== 0 && openUntil <= now) {
      this.resetFailures();
      this.breakerOpenUntil.set(0);
    }

    return true;
  }

  /**
   * Track eligible failures and trip breaker when threshold is met.
   */
  recordFailure(error: HttpErrorResponse, url: string): FailureRecordResult {
    if (this.isAllowlisted(url) || !this.shouldCountFailure(error)) {
      return { tripped: false };
    }

    const now = Date.now();
    const firstAt = this.firstFailureAt();
    const withinWindow = firstAt !== 0 && now - firstAt <= this.failureWindowMs;

    if (!withinWindow) {
      this.firstFailureAt.set(now);
      this.consecutiveFailures.set(1);
    } else {
      this.consecutiveFailures.set(this.consecutiveFailures() + 1);
    }

    if (this.consecutiveFailures() >= this.failureThreshold) {
      this.breakerOpenUntil.set(now + this.cooldownMs);
      this.resetFailures();
      return { tripped: true };
    }

    return { tripped: false };
  }

  /**
   * Success on guarded endpoints resets failure streak and closes breaker.
   */
  recordSuccess(url: string): void {
    if (this.isAllowlisted(url)) {
      return;
    }

    this.resetFailures();
    this.breakerOpenUntil.set(0);
  }

  /**
   * Remaining block time in milliseconds (0 if closed).
   */
  timeRemainingMs(): number {
    const remaining = this.breakerOpenUntil() - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  isOpen(): boolean {
    return this.timeRemainingMs() > 0;
  }

  private resetFailures(): void {
    this.firstFailureAt.set(0);
    this.consecutiveFailures.set(0);
  }

  private isAllowlisted(url: string): boolean {
    return this.allowlistedPathFragments.some((fragment) => url.includes(fragment));
  }

  private shouldCountFailure(error: HttpErrorResponse): boolean {
    // Never count client/business errors.
    if ([400, 401, 403, 404, 422].includes(error.status)) {
      return false;
    }

    // Count likely backend outage statuses.
    if ([0, 500, 502, 503, 504].includes(error.status)) {
      // Exclude synthetic client-side breaker blocks.
      if ((error.error as { circuitBreakerOpen?: boolean } | null)?.circuitBreakerOpen) {
        return false;
      }
      return true;
    }

    return false;
  }
}
